import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../../../auth/AuthContext";
import { getFundDbName } from "../../../../../utils/funds";
import { imileCheckoutService } from "../services/imile-checkout-service";
import { ImileCheckoutItem } from "../types";

export const imileCheckoutKeys = {
  all: ["imile-checkout"] as const,
  list: (dbName: string) => [...imileCheckoutKeys.all, "list", dbName] as const,
};

export function useImileCheckout() {
  const { user } = useAuth();
  const xamaDbName = getFundDbName("imile");

  const {
    data: checkoutList = [],
    isPending,
    refetch,
  } = useQuery({
    queryKey: imileCheckoutKeys.list(xamaDbName),
    queryFn: async (): Promise<ImileCheckoutItem[]> => {
      const response = await imileCheckoutService.getCheckoutList({
        dbName: xamaDbName,
        opsMae: "imile",
      });

      // Uma query cobre a tela inteira: arquivos por base, métricas de ticket e a
      // conferência saem agregados por data em jsonb, na mesma varredura.
      return (response.data || []).map(
        ({ files_processed, ...checkout }): ImileCheckoutItem => ({
          ...checkout,
          filesProcessed: files_processed || [],
        })
      );
    },
    staleTime: 1000 * 60 * 2,
    refetchInterval: 1000 * 120,
  });

  const confirmCheckoutMutation = useMutation({
    mutationFn: async ({
      filterDate,
      ops,
    }: {
      filterDate: string;
      ops: string;
    }) => {
      if (!xamaDbName || !user?.email) {
        throw new Error("Necessário e-mail do gestor e DBName");
      }
      // Sem offerAction: o envio ativo pós-registro é piloto do entrego.
      return imileCheckoutService.confirmCheckout({
        dbName: xamaDbName,
        filterDate,
        userInfo: { email: user.email },
        ops,
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        refetch();
      }, 3000);
    },
  });

  const startDocMutation = useMutation({
    mutationFn: async ({
      filterDate,
      ops,
    }: {
      filterDate: string;
      ops: string;
    }) => {
      if (!xamaDbName || !user?.email) {
        throw new Error("Necessário e-mail do gestor e DBName");
      }

      return imileCheckoutService.startDoc({
        dbName: xamaDbName,
        filterDate,
        userInfo: { email: user.email },
        ops,
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        refetch();
      }, 3000);
    },
  });

  const submitRemessaMutation = useMutation({
    mutationFn: async ({
      filterDate,
      ops,
    }: {
      filterDate: string;
      ops: string;
    }) => {
      if (!xamaDbName || !user?.email) {
        throw new Error("Necessário e-mail do gestor e DBName");
      }

      return imileCheckoutService.submitRemessa({
        dbName: xamaDbName,
        filterDate,
        userInfo: { email: user.email },
        ops,
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        refetch();
      }, 3000);
    },
  });

  return {
    checkoutList,
    // isPending e não isLoading: isLoading do react-query v5 é
    // `isPending && isFetching` e volta false em qualquer janela sem fetch em
    // voo, deixando o estado vazio vazar na tela.
    isLoading: isPending,
    xamaDbName,
    confirmCheckout: confirmCheckoutMutation.mutate,
    startDoc: startDocMutation.mutate,
    submitRemessa: submitRemessaMutation.mutate,
    isConfirming: confirmCheckoutMutation.isPending,
    isStartingDoc: startDocMutation.isPending,
    isSubmittingRemessa: submitRemessaMutation.isPending,
    refetch,
  };
}
